import * as React from "react";
import styled, { css } from "styled-components";

import Icon, { IconTypes } from "./Icon";
import { media, mediaMax, space } from "./common";
import { theme } from "./theme";

interface AlertTheme {
  iconType: IconTypes;
  highlightColor: string; // icon and border color
  textColor: string;
  bgColor: string;
}

const errorTheme: AlertTheme = {
  iconType: "warning-lg",
  highlightColor: theme.errorRed,
  textColor: theme.errorRed,
  bgColor: theme.errorBgRed,
};

const warningTheme: AlertTheme = {
  iconType: "warning-lg",
  highlightColor: "#ffb200",
  textColor: "#333f48",
  bgColor: "#fff8e6",
};

const successTheme: AlertTheme = {
  iconType: "success",
  highlightColor: "#00c694",
  textColor: theme.textColor,
  bgColor: "#daf5ed",
};

const infoTheme: AlertTheme = {
  iconType: "warning-lg",
  highlightColor: "#055fb4",
  textColor: "#333f48",
  bgColor: theme.bgLightBlue,
};

const urgentTheme: AlertTheme = {
  iconType: "warning-lg",
  highlightColor: "#D43900",
  textColor: "#333f48",
  bgColor: "#D439001A",
};

type ThemeType = "error" | "warning" | "success" | "info" | "urgent";
type AlertThemes = { [key in ThemeType]: AlertTheme };
const themes: AlertThemes = {
  error: errorTheme,
  warning: warningTheme,
  success: successTheme,
  info: infoTheme,
  urgent: urgentTheme,
};

export interface AlertProps {
  type?: ThemeType;
  fix?: boolean;
  iconType?: IconTypes;
  closable?: boolean;
  show?: boolean;
  closeCallBack?: () => void;
  title?: string;
  width?: number | string;
  className?: string;
  ref?: React.Ref<Alert>;
  alignSelf?: string;
  padding?: string;
  margin?: string;
  alignItems?: string;
  smAlignItems?: string;
  showBorderLeftAndRight?: boolean;
  showBorderRadius?: boolean;
}

interface State {
  show?: boolean;
}

class Alert extends React.Component<AlertProps, State> {
  static defaultProps = {
    closable: false,
  };

  readonly state = {
    show: this.props.show === undefined ? true : this.props.show,
  };

  componentDidUpdate(_: AlertProps, prevState: State) {
    const { show } = this.props;
    if (show !== undefined && prevState.show !== show) {
      this.setState({ show });
    }
  }

  public render() {
    const {
      type = "error",
      fix = false,
      width,
      children,
      closable,
      title,
      className,
      padding,
      alignItems,
      smAlignItems,
      showBorderLeftAndRight = true,
      showBorderRadius = true,
      margin,
    } = this.props;
    const theme = themes[type];
    const iconType = this.props.iconType || theme.iconType;
    const calcWidth = typeof width === "number" ? width + "px" : width || "";
    const alignSelf = this.props.alignSelf || "auto";
    return this.state.show ? (
      <AlertWrapper
        type={type}
        fix={fix}
        width={calcWidth}
        className={className}
        padding={padding}
        alignItems={alignItems}
        smAlignItems={smAlignItems}
        showBorderLeftAndRight={showBorderLeftAndRight}
        showBorderRadius={showBorderRadius}
        margin={margin}
      >
        <AlertIcon
          type={iconType}
          color={theme.highlightColor}
          alignSelf={alignSelf}
        />
        <AlertBody>
          {title && <Title>{title}</Title>}
          {children}
        </AlertBody>
        {closable && <CloseBtn type="close" onClick={this.handleClose} />}
      </AlertWrapper>
    ) : null;
  }

  private handleClose = () => {
    this.setState({ show: false });
    if (this.props.closeCallBack) {
      this.props.closeCallBack();
    }
  };
}

export const Title = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
`;

const ErrorBox: React.FC<AlertProps> = React.forwardRef((props, ref) => {
  return <Alert type="error" {...props} ref={ref} />;
});
export { Alert, ErrorBox };

export const AlertWrapper = styled.div<{
  type: ThemeType;
  fix: boolean;
  width: string;
  padding?: string;
  alignItems?: string;
  smAlignItems?: string;
  showBorderLeftAndRight?: boolean;
  showBorderRadius?: boolean;
  margin?: string;
}>`
  display: flex;
  align-items: ${({ alignItems }) => alignItems || "stretch"};
  ${({ smAlignItems }) => mediaMax.sm`
    align-items: ${smAlignItems || "baseline"}
  `}
  width: ${({ width }) => width || "auto"};
  padding: ${({ padding }) => padding || "10px"};
  line-height: 20px;
  border: 1px solid ${(props) => themes[props.type].highlightColor};
  border-radius: ${(props) => (props.showBorderRadius ? "3px" : 0)};
  background-color: ${(props) => themes[props.type].bgColor};
  ${({ fix, margin }) =>
    fix
      ? css`
          position: fixed;
          transform: translateX(-50%);
          left: 50%;
          top: 25px;
          z-index: 1000;
        `
      : css`
          position: relative;
          margin: ${margin || `${space}px 0`};
          ${media.lg`
              margin:${margin || "0 0 20px"};
          `};
        `}

  ${({ showBorderLeftAndRight }) =>
    !showBorderLeftAndRight &&
    css`
      border-left: 0;
      border-right: 0;
    `};
`;

const AlertIcon = styled(Icon)<{
  alignSelf: string;
}>`
  line-height: 20px;
  text-align: center;
  margin-right: ${space}px;
  font-size: ${(props) => props?.theme?.fontSize?.title2};
  align-self: ${({ alignSelf }) => alignSelf || "auto"};
`;

const AlertBody = styled.div`
  flex: 1 1 0%;
  font-size: ${(props) => props?.theme?.fontSize?.body};
  line-height: 24px;
`;

const CloseBtn = styled(Icon)`
  line-height: 20px;
  text-align: center;
  margin-left: ${space}px;
  color: ${theme.textDeepGray};
  &:hover {
    cursor: pointer;
    color: ${theme.textColor};
  }
`;
